import { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import FilterStore from "../../hooks/customHook/FilterStore";

const readStoredTheme = () => {
  try {
    return FilterStore.getTheme();
  } catch (error) {
    return "light";
  }
};

let currentTheme = readStoredTheme();
const listeners = new Set();

export const applyTheme = (theme) => {
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.setAttribute("data-bs-theme", theme);
};

export const getTheme = () => currentTheme;

export const setTheme = (theme) => {
  currentTheme = theme === "dark" ? "dark" : "light";
  try {
    FilterStore.saveTheme(currentTheme);
  } catch (error) {
    console.error(error);
  }
  applyTheme(currentTheme);
  listeners.forEach((listener) => listener(currentTheme));
};

// Toggles the theme. Given the click event, the new theme spreads out as a
// circle from the click point (View Transitions API); browsers without it,
// or users who prefer reduced motion, just switch instantly
export const toggleTheme = (event) => {
  const nextTheme = currentTheme === "dark" ? "light" : "dark";
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (!document.startViewTransition || reduceMotion) {
    setTheme(nextTheme);
    return;
  }

  const x = event?.clientX ?? window.innerWidth / 2;
  const y = event?.clientY ?? window.innerHeight / 2;
  // Distance to the farthest corner, so the circle covers the whole screen
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y),
  );

  // CSS color transitions (Bootstrap, `transition: all`) would make the new
  // snapshot still show the old colors, so the circle reveals nothing. Turn
  // them off until the animation ends
  const root = document.documentElement;
  root.classList.add("theme-switching");

  // flushSync so React re-renders (button label, icons) before the new
  // snapshot is taken
  const transition = document.startViewTransition(() => {
    flushSync(() => setTheme(nextTheme));
  });
  transition.finished.finally(() => root.classList.remove("theme-switching"));

  transition.ready.then(() => {
    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${radius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 500,
        easing: "ease-in-out",
        pseudoElement: "::view-transition-new(root)",
      },
    );
  });
};

applyTheme(currentTheme);

// react-select takes its colors from JS style objects, so the
// [data-theme="dark"] CSS can't reach it. In dark mode this wraps the output of
// selectCustomStyles and overrides only the colors (borders, validation and
// sizing from the base styles are kept). In light mode the styles are returned
// untouched.
//
// Pages normally use it through the hook:
//   const { themeSelectStyles } = useDarkMode();
//   <Select styles={themeSelectStyles(selectCustomStyles(value))} />
export const withDarkSelectStyles = (styles, isDark) => {
  if (!isDark) return styles;

  const merge = (key, getExtra) => (base, state) => ({
    ...(styles[key] ? styles[key](base, state) : base),
    ...getExtra(state),
  });

  return {
    ...styles,
    control: merge("control", (state) => ({
      backgroundColor: state.isDisabled
        ? "var(--dm-surface)"
        : "var(--dm-surface-2)",
      color: "var(--dm-text)",
    })),
    menu: merge("menu", () => ({
      backgroundColor: "var(--dm-surface-2)",
      border: "1px solid var(--dm-border)",
    })),
    menuList: merge("menuList", () => ({
      backgroundColor: "var(--dm-surface-2)",
    })),
    option: merge("option", (state) => ({
      backgroundColor: state.isSelected
        ? "var(--dm-accent-bg)"
        : state.isFocused
          ? "var(--dm-surface)"
          : "transparent",
      color: state.isSelected ? "var(--dm-accent)" : "var(--dm-text)",
      cursor: "pointer",
    })),
    singleValue: merge("singleValue", () => ({ color: "var(--dm-text)" })),
    input: merge("input", () => ({ color: "var(--dm-text)" })),
    placeholder: merge("placeholder", () => ({
      color: "var(--dm-text-muted)",
    })),
    noOptionsMessage: merge("noOptionsMessage", () => ({
      color: "var(--dm-text-muted)",
    })),
    indicatorSeparator: merge("indicatorSeparator", () => ({
      backgroundColor: "var(--dm-border)",
    })),
    dropdownIndicator: merge("dropdownIndicator", () => ({
      color: "var(--dm-text-muted)",
    })),
    clearIndicator: merge("clearIndicator", () => ({
      color: "var(--dm-text-muted)",
    })),
  };
};

const useDarkMode = () => {
  const [theme, setThemeState] = useState(currentTheme);

  useEffect(() => {
    listeners.add(setThemeState);
    setThemeState(currentTheme);
    return () => {
      listeners.delete(setThemeState);
    };
  }, []);

  return {
    theme,
    isDark: theme === "dark",
    toggleDarkMode: toggleTheme,
    setDarkMode: (isDark) => setTheme(isDark ? "dark" : "light"),
    themeSelectStyles: (styles) =>
      withDarkSelectStyles(styles, theme === "dark"),
  };
};

export default useDarkMode;
